import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Avatar,
  Stack,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  Person,
  Event,
  AccessTime,
  CheckCircle,
  Cancel,
  Schedule,
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, isToday, isSameWeek } from 'date-fns';
import { apiService } from '../../services/api';
import { Leave, LeaveStatus, LeaveType } from '../../types';

interface LeaveCalendarProps {
  onLeaveClick?: (leave: Leave) => void;
  showAllEmployees?: boolean;
}

const LeaveCalendar: React.FC<LeaveCalendarProps> = ({ 
  onLeaveClick, 
  showAllEmployees = true 
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
  const [leaveDetailsOpen, setLeaveDetailsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [dayLeavesDialogOpen, setDayLeavesDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    employeeId: '',
  });

  useEffect(() => {
    loadLeaves();
  }, [currentDate, filters]);

  const loadLeaves = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const startDate = startOfMonth(currentDate).toISOString().split('T')[0];
      const endDate = endOfMonth(currentDate).toISOString().split('T')[0];
      
      let data: Leave[];
      if (showAllEmployees) {
        // For managers/admins - get all leaves
        data = await apiService.getAllLeaves({ startDate, endDate, ...filters });
      } else {
        // For employees - get their own leaves
        data = await apiService.getUserLeaves({ startDate, endDate, ...filters });
      }
      
      setLeaves(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load leaves:', error);
      setError('Failed to load leave data');
      setLeaves([]);
    } finally {
      setLoading(false);
    }
  };

  const getLeaveTypeColor = (type: LeaveType) => {
    switch (type) {
      case LeaveType.FULL_DAY:
        return 'primary';
      case LeaveType.HALF_DAY:
        return 'secondary';
      case LeaveType.HOURS:
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: LeaveStatus) => {
    switch (status) {
      case LeaveStatus.APPROVED:
        return 'success';
      case LeaveStatus.REJECTED:
        return 'error';
      case LeaveStatus.PENDING:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getLeaveTypeLabel = (type: LeaveType) => {
    switch (type) {
      case LeaveType.FULL_DAY:
        return 'Full Day';
      case LeaveType.HALF_DAY:
        return 'Half Day';
      case LeaveType.HOURS:
        return 'Hours';
      default:
        return type;
    }
  };

  const getStatusIcon = (status: LeaveStatus) => {
    switch (status) {
      case LeaveStatus.APPROVED:
        return <CheckCircle fontSize="small" />;
      case LeaveStatus.REJECTED:
        return <Cancel fontSize="small" />;
      case LeaveStatus.PENDING:
        return <Schedule fontSize="small" />;
      default:
        return <Event fontSize="small" />;
    }
  };

  const handlePreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleLeaveClick = (leave: Leave) => {
    setSelectedLeave(leave);
    setLeaveDetailsOpen(true);
    onLeaveClick?.(leave);
  };

  const getLeavesForDate = (date: Date) => {
    return leaves.filter(leave => {
      const startDate = new Date(leave.startDate);
      const endDate = new Date(leave.endDate);
      return date >= startDate && date <= endDate;
    });
  };

  const handleDayClick = (date: Date) => {
    const dayLeaves = getLeavesForDate(date);
    if (dayLeaves.length > 0) {
      setSelectedDate(date);
      setDayLeavesDialogOpen(true);
    }
  };

  const renderCalendarDay = (date: Date) => {
    const dayLeaves = getLeavesForDate(date);
    const isCurrentMonth = isSameMonth(date, currentDate);
    const isCurrentDay = isToday(date);

    return (
      <Box
        key={date.toISOString()}
        onClick={() => handleDayClick(date)}
        sx={{
          minHeight: 140,
          p: 1,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: isCurrentDay ? 'action.hover' : 'background.paper',
          opacity: isCurrentMonth ? 1 : 0.5,
          cursor: dayLeaves.length > 0 ? 'pointer' : 'default',
          position: 'relative',
          '&:hover': {
            backgroundColor: dayLeaves.length > 0 ? 'action.hover' : 'background.paper',
            boxShadow: dayLeaves.length > 0 ? 2 : 0,
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: isCurrentDay ? 'bold' : 'normal',
            color: isCurrentDay ? 'primary.main' : 'text.primary',
            mb: 1,
          }}
        >
          {format(date, 'd')}
        </Typography>
        
        <Stack spacing={0.5} sx={{ maxHeight: 100, overflowY: 'auto' }}>
          {dayLeaves.map((leave) => {
            const employeeName = leave.user 
              ? `${leave.user.firstName || ''} ${leave.user.lastName || ''}`.trim() 
              : leave.userId || 'Unknown';
            
            return (
              <Tooltip
                key={leave.id}
                title={`${employeeName} - ${getLeaveTypeLabel(leave.type)} (${leave.status})`}
                arrow
              >
                <Chip
                  label={employeeName.length > 12 ? `${employeeName.substring(0, 10)}...` : employeeName}
                  size="small"
                  color={getLeaveTypeColor(leave.type) as any}
                  variant={leave.status === LeaveStatus.REJECTED ? 'outlined' : 'filled'}
                  icon={getStatusIcon(leave.status)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLeaveClick(leave);
                  }}
                  sx={{
                    fontSize: '0.7rem',
                    height: 22,
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                    '& .MuiChip-icon': {
                      fontSize: '0.8rem',
                    },
                  }}
                />
              </Tooltip>
            );
          })}
          {dayLeaves.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No leaves
            </Typography>
          )}
        </Stack>
      </Box>
    );
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <Box>
        {/* Calendar Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <IconButton onClick={handlePreviousMonth}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
            {format(currentDate, 'MMMM yyyy')}
          </Typography>
          <Button
            startIcon={<Today />}
            onClick={handleToday}
            variant="outlined"
            size="small"
          >
            Today
          </Button>
          <IconButton onClick={handleNextMonth}>
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Filters */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value={LeaveStatus.PENDING}>Pending</MenuItem>
              <MenuItem value={LeaveStatus.APPROVED}>Approved</MenuItem>
              <MenuItem value={LeaveStatus.REJECTED}>Rejected</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Calendar Grid */}
        <Paper sx={{ overflow: 'hidden' }}>
          {/* Week Days Header */}
          <Grid container>
            {weekDays.map((day) => (
              <Grid item xs key={day}>
                <Box
                  sx={{
                    p: 1,
                    textAlign: 'center',
                    backgroundColor: 'grey.100',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    fontWeight: 'bold',
                  }}
                >
                  <Typography variant="body2">{day}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Calendar Days */}
          <Grid container>
            {days.map((date) => (
              <Grid item xs key={date.toISOString()}>
                {renderCalendarDay(date)}
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>
    );
  };

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (
        renderCalendar()
      )}

      {/* Day Leaves Dialog */}
      <Dialog
        open={dayLeavesDialogOpen}
        onClose={() => setDayLeavesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Leaves for {selectedDate && format(selectedDate, 'MMMM dd, yyyy')}
        </DialogTitle>
        <DialogContent>
          {selectedDate && (
            <Box sx={{ mt: 2 }}>
              {getLeavesForDate(selectedDate).length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                  No leaves on this date
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {getLeavesForDate(selectedDate).map((leave) => {
                    const employeeName = leave.user 
                      ? `${leave.user.firstName || ''} ${leave.user.lastName || ''}`.trim() 
                      : leave.userId || 'Unknown';
                    
                    return (
                      <Card key={leave.id} sx={{ cursor: 'pointer' }} onClick={() => {
                        setDayLeavesDialogOpen(false);
                        handleLeaveClick(leave);
                      }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 32, height: 32 }}>
                                {employeeName.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1" fontWeight="medium">
                                  {employeeName}
                                </Typography>
                                {leave.user?.email && (
                                  <Typography variant="caption" color="text.secondary">
                                    {leave.user.email}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'flex-end' }}>
                              <Chip
                                label={getLeaveTypeLabel(leave.type)}
                                color={getLeaveTypeColor(leave.type) as any}
                                size="small"
                              />
                              <Chip
                                label={leave.status}
                                color={getStatusColor(leave.status) as any}
                                size="small"
                                icon={getStatusIcon(leave.status)}
                              />
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>From:</strong> {format(new Date(leave.startDate), 'MMM dd, yyyy')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>To:</strong> {format(new Date(leave.endDate), 'MMM dd, yyyy')}
                            </Typography>
                            {leave.type === LeaveType.HOURS && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Hours:</strong> {leave.hours}
                              </Typography>
                            )}
                          </Box>
                          {leave.reason && (
                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                              <strong>Reason:</strong> {leave.reason}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDayLeavesDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Leave Details Dialog */}
      <Dialog
        open={leaveDetailsOpen}
        onClose={() => setLeaveDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Leave Details
        </DialogTitle>
        <DialogContent>
          {selectedLeave && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar>
                      <Person />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {selectedLeave.user?.firstName} {selectedLeave.user?.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedLeave.user?.email}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Leave Type
                  </Typography>
                  <Chip
                    label={getLeaveTypeLabel(selectedLeave.type)}
                    color={getLeaveTypeColor(selectedLeave.type) as any}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedLeave.status}
                    color={getStatusColor(selectedLeave.status) as any}
                    size="small"
                    icon={getStatusIcon(selectedLeave.status)}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(selectedLeave.startDate), 'MMM dd, yyyy')}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    End Date
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(selectedLeave.endDate), 'MMM dd, yyyy')}
                  </Typography>
                </Grid>
                
                {selectedLeave.type === LeaveType.HOURS && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Hours
                    </Typography>
                    <Typography variant="body1">
                      {selectedLeave.hours} hours
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Reason
                  </Typography>
                  <Typography variant="body1">
                    {selectedLeave.reason}
                  </Typography>
                </Grid>
                
                {selectedLeave.approvedBy && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Approved By
                    </Typography>
                    <Typography variant="body1">
                      {selectedLeave.approver?.firstName} {selectedLeave.approver?.lastName}
                    </Typography>
                  </Grid>
                )}
                
                {selectedLeave.rejectionReason && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Rejection Reason
                    </Typography>
                    <Typography variant="body1" color="error">
                      {selectedLeave.rejectionReason}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveDetailsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveCalendar;
